import React from 'react';

import { Button, Group, Modal, Stack, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconTags } from '@tabler/icons-react';
import useCreateOtherWorkType from 'hooks/otherWorkType/useCreateOtherWorkType';
import { OtherWorkTypeWrite } from 'types';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

interface IModalWriteOtherWorkTypeProps {
  opened: boolean;
  handleClose: () => void;
}

interface FormValues {
  name: string;
}

const ModalWriteOtherWorkType = ({ opened, handleClose }: IModalWriteOtherWorkTypeProps): JSX.Element => {
  const createMutation = useCreateOtherWorkType();

  const form = useForm<FormValues>({
    initialValues: {
      name: '',
    },

    validate: {
      name: value =>
        value.length < 3 || value.length > 120 ? 'Name must be between 3-120 characters' : null,
    },
  });

  const handleCloseAndReset = (): void => {
    handleClose();
    form.reset();
  };

  const { successNotification, errorNotification } = notificationHandler();
  const handleCreate = (values: FormValues): void => {
    const payload: OtherWorkTypeWrite = {
      otherWorkTypeId: uuidv4(),
      ...values,
    };
    createMutation.mutate(payload, {
      onSuccess: () => {
        successNotification(
          'Creating other work type success',
          `${values.name} has been successfully created.`,
        );
        handleCloseAndReset();
      },
      onError: error => {
        errorNotification('Creating other work type failed', error.response?.data.message);
      },
    });
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleCloseAndReset}
      title={<Title order={4}>Create Other Work Type</Title>}
      size='auto'
    >
      <form onSubmit={form.onSubmit(handleCreate)}>
        <Stack>
          <TextInput
            required
            label='Name'
            {...form.getInputProps('name')}
            icon={<IconTags />}
            placeholder='Set Work Type Name'
            data-cy='otherWorkTypeTextInput'
          />
          <Group position='right'>
            <Button variant='subtle' onClick={handleCloseAndReset} disabled={createMutation.isLoading}>
              Cancel
            </Button>
            <Button disabled={createMutation.isLoading} type='submit' data-cy='saveOtherWorkTypeButton'>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default ModalWriteOtherWorkType;
